// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 *
 * <HeaderClickable
 *      iconSrc={icon}
 *      onClick={() => !isMobile && history.push(cartUrl ?? "")}
 *      iconProps={{ type: "miniCart" }}
 *      clickableProps={{ "data-test-selector": "cartLink" }}
 *      titleProps={{
 *          "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
 *      }}
 *      >
 *          <>
 *              {`${translate("Top Message")}`}
 *              <Typography className="sub-title">{`${translate("Bottom Message")}`}</Typography>
 *          </>
 *      </HeaderClickable>
 */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { color, ellipsis, hideElement, mergeWithCss, xClassNames, xScreen } from "@werner/XDL/Utilities";
import React, { FC, MouseEventHandler } from "react";
import { useState } from "react";
import { css } from "styled-components";

interface HeaderClickableProps {
    onClick?: MouseEventHandler<any>;
    iconProps?: IconProps;
    titleProps?: TypographyProps;
    title?: string;
    subTitle?: string;
    isOpen?: boolean;
    extendedStyles?: HeaderClickableStyle;
}

type Props = HeaderClickableProps & ClickableProps;

export interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    cartItem?: {
        wrapper?: InjectableCss;
        text?: TypographyProps;
    };
}

const headerClickableStyles: HeaderClickableStyle = {
    clickable: {
        css: css`
            ${IconWrapper} {
                color: ${color("common.ui3")};
            }
            &:hover,
            &.opened {
                ${TypographyStyle} {
                    color: ${color("primary")};
                    cursor: pointer;
                }
                ${IconWrapper} {
                    color: ${color("primary")};
                    cursor: pointer;
                }
            }
            &.mobile-mode {
                display: flex;
                justify-content: center;
                ${xScreen("lte", "md")} {
                    width: 30px;
                    height: 30px;
                }
                ${xScreen("lte", "sm")} {
                    width: 24px;
                    height: 24px;
                }
                ${IconWrapper} {
                    color: ${color("common.ui3")};
                }
            }
            &.mobile-mode:empty {
                display: none;
            }

            &:focus {
                outline: none;
            }
        `,
    },
    title: {
        size: 16,
        color: "text.main",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            display: block;
            max-width: 150px;
            margin-left: 12px;
            text-transform: capitalize;
            white-space: nowrap;
            &:not(.break-words) {
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .break-words {
                white-space: pre-wrap;
            }

            & > .sub-title {
                display: block;
                white-space: nowrap;
                font-weight: 400;
                line-height: 18px;
                margin-bottom: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                color: ${color("common.ui3")};
                font-size: 14px;
            }

            &:hover {
                cursor: pointer;
                color: ${color("primary")};
            }

            ${xScreen("lte", "md")} {
                ${hideElement}
            }
            &.sticky {
                ${hideElement}
            }
        `,
    },
    icon: {
        size: 28,
        color: "common.ui3",
    },
};

const HeaderClickable: FC<Props> = ({
    iconProps,
    titleProps,
    title,
    subTitle,
    extendedStyles,
    isOpen,
    ...otherProps
}) => {
    const { isTabletScreen, isMobileScreen, isSticky } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        isSticky: (state as CustomAppState).xHeader.isSticky,
    }));
    const classNames = xClassNames.append([isTabletScreen && "mobile-mode", isOpen && "opened"], otherProps.className);
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));

    return (
        <Clickable {...styles.clickable} {...otherProps} className={classNames} aria-label={`${title} ${subTitle}`}>
            {iconProps && (
                <Icon
                    {...styles.icon}
                    {...iconProps}
                    size={isMobileScreen ? 24 : 30}
                    className="menu-icon"
                    title={title}
                />
            )}
            <Typography {...styles.title} {...titleProps} className={xClassNames.append([isSticky && "sticky"])}>
                {title && <Typography>{title}</Typography>}
                {subTitle && <Typography className="sub-title">{subTitle}</Typography>}
            </Typography>
        </Clickable>
    );
};

export default HeaderClickable;
